/**
 * CompetitionAnalysisDialog.jsx — the quote's lines against VirTour Media,
 * Framed by JG and 805 Media Works, priced at the same square footage on their
 * current lists.
 *
 * Internal only: opened from the quote builder, never on the PDF, the email or
 * the hosted quote. The math is buildCompetitionAnalysis (line by line, no
 * package deals); this file only lays it out.
 */

import { useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Table, TableHead, TableBody, TableRow, TableCell, Alert } from '@mui/material';
import { fmtC, fmtD } from '../../lib/utils';
import { useMobile } from '../../hooks/useMobile';
import { buildCompetitionAnalysis } from '../../lib/competitionAnalysis';

export const head = { fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', verticalAlign: 'bottom' };
export const small = { fontSize: '0.72rem', lineHeight: 1.35 };

export function Delta({ theirs, ours }) {
  if (!(ours > 0)) return null;
  const diff = theirs - ours;
  if (Math.abs(diff) < 0.005) return <Typography component="div" sx={{ ...small, color: 'text.secondary' }}>Same as yours</Typography>;
  const pct = Math.round((diff / ours) * 100);
  return (
    <Typography component="div" sx={{ ...small, color: diff > 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
      {diff > 0 ? '+' : '−'}{fmtC(Math.abs(diff))} ({diff > 0 ? '+' : '−'}{Math.abs(pct)}%)
    </Typography>
  );
}

export function CompetitorCell({ cell, ours }) {
  const note = cell.note && (
    <Typography component="div" sx={{ ...small, color: 'text.secondary', fontStyle: 'italic', mt: 0.25 }}>{cell.note}</Typography>
  );
  if (cell.status === 'priced' || cell.status === 'inquire') {
    return (
      <>
        <Typography component="div" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
          {cell.status === 'inquire' ? 'Inquire' : fmtC(cell.price)}
        </Typography>
        {cell.items.map((it, i) => (
          <Typography key={i} component="div" sx={{ ...small, color: 'text.primary' }}>
            {it.asAddon ? '+ ' : ''}{it.name}
            {it.qty > 1 ? ` × ${it.qty}` : ''}
            {cell.items.length > 1 || it.qty > 1 ? ` · ${it.unit == null ? 'inquire' : fmtC(it.unit * it.qty)}` : ''}
          </Typography>
        ))}
        {cell.status === 'priced' && <Delta theirs={cell.price} ours={ours} />}
        {note}
      </>
    );
  }
  const label = { included: 'Included', none: 'Not offered', unmapped: 'No match' }[cell.status];
  return (
    <>
      <Typography component="div" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
      {note}
    </>
  );
}

// Phones: one card per line, the competitors stacked under it, since three
// price columns don't fit side by side.
function MobileList({ rows, totals, competitors, sqft }) {
  const label = (text) => (
    <Typography component="div" sx={{ ...small, color: 'text.secondary', fontWeight: 600, pt: 0.25 }}>{text}</Typography>
  );
  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <Box sx={{ ...small, color: 'text.secondary' }}>
        {competitors.map((c) => (
          <div key={c.id}>{c.name}: {c.tierLabel(sqft)} · prices {fmtD(c.asOf)}</div>
        ))}
      </Box>
      {rows.map((r) => (
        <Box key={r.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, ml: r.isAddon ? 2 : 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {r.isAddon ? '+ ' : ''}{r.desc}{r.qty > 1 ? ` × ${r.qty}` : ''}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtC(r.ours)}</Typography>
          </Box>
          {r.flash && (
            <Typography component="div" sx={{ ...small, color: 'text.secondary', mt: -0.75, mb: 1 }}>
              {r.flash.name}: {fmtC(r.flash.price)}
            </Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '84px 1fr', gap: '8px 10px' }}>
            {competitors.map((c) => [
              <Box key={`${c.id}-l`}>{label(c.name)}</Box>,
              <Box key={`${c.id}-v`}><CompetitorCell cell={r.cells[c.id]} ours={r.ours} /></Box>,
            ])}
          </Box>
        </Box>
      ))}
      <Box sx={{ border: 2, borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: 'action.hover' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>Total, every line</Typography>
          <Typography sx={{ fontWeight: 700 }}>{fmtC(totals.ours)}</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '84px 1fr', gap: '8px 10px' }}>
          {competitors.map((c) => {
            const t = totals[c.id];
            const left = t.unmatched + t.inquire;
            return [
              <Box key={`${c.id}-l`}>{label(c.name)}</Box>,
              <Box key={`${c.id}-v`}>
                <Typography component="div" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{fmtC(t.theirs)}</Typography>
                {left > 0 && (
                  <Typography component="div" sx={{ ...small, color: 'text.secondary' }}>{left} of your lines not priced by them</Typography>
                )}
                <Typography component="div" sx={{ ...small, mt: 0.5 }}>
                  Like for like: theirs <b>{fmtC(t.theirs)}</b> vs yours <b>{fmtC(t.oursMatched)}</b>
                </Typography>
                <Delta theirs={t.theirs} ours={t.oursMatched} />
              </Box>,
            ];
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default function CompetitionAnalysisDialog({ state, computed, onClose }) {
  const { isMobile } = useMobile();
  const analysis = useMemo(
    () => buildCompetitionAnalysis({ lines: computed.activeLines, sqft: state.sqft }),
    [computed.activeLines, state.sqft]
  );
  const { rows, totals, competitors, sqft, sqftMissing } = analysis;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" component="div">Competition analysis</Typography>
        <Typography variant="body2" color="text.secondary">
          {[state.address, sqftMissing ? null : `${sqft.toLocaleString()} sq ft`].filter(Boolean).join(' · ')}
          {' · '}Line by line, their current prices, no package deals
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {sqftMissing && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No square footage on the quote, so sized prices are at their smallest rung.
          </Alert>
        )}

        {isMobile ? (
          <MobileList rows={rows} totals={totals} competitors={competitors} sqft={sqft} />
        ) : (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 980, '& td': { verticalAlign: 'top' } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ ...head, width: '22%' }}>Your service</TableCell>
                <TableCell align="right" sx={{ ...head, width: '12%' }}>Yours</TableCell>
                {competitors.map((c) => (
                  <TableCell key={c.id} sx={{ ...head, width: '22%' }}>
                    {c.name}
                    <Typography component="div" sx={{ ...small, textTransform: 'none', fontWeight: 400, color: 'text.secondary' }}>
                      {c.tierLabel(sqft)} · prices {fmtD(c.asOf)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell sx={{ fontSize: '0.82rem', pl: r.isAddon ? 3.5 : 2 }}>
                    {r.isAddon ? '+ ' : ''}{r.desc}
                    {r.qty > 1 && (
                      <Typography component="div" sx={{ ...small, color: 'text.secondary' }}>× {r.qty}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    {fmtC(r.ours)}
                    {r.flash && (
                      <Typography component="div" sx={{ ...small, color: 'text.secondary', fontWeight: 400 }}>
                        {r.flash.name}: {fmtC(r.flash.price)}
                      </Typography>
                    )}
                  </TableCell>
                  {competitors.map((c) => (
                    <TableCell key={c.id}>
                      <CompetitorCell cell={r.cells[c.id]} ours={r.ours} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              <TableRow sx={{ bgcolor: 'action.hover', '& td': { borderTop: 2, borderColor: 'divider' } }}>
                <TableCell sx={{ fontWeight: 700 }}>Total, every line</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtC(totals.ours)}</TableCell>
                {competitors.map((c) => {
                  const t = totals[c.id];
                  const left = t.unmatched + t.inquire;
                  return (
                    <TableCell key={c.id}>
                      <Typography component="div" sx={{ fontWeight: 700 }}>{fmtC(t.theirs)}</Typography>
                      {left > 0 && (
                        <Typography component="div" sx={{ ...small, color: 'text.secondary' }}>
                          {left} of your lines not priced by them
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>
                  Like for like
                  <Typography component="div" sx={{ ...small, color: 'text.secondary', fontWeight: 400 }}>
                    Only the lines they price or include
                  </Typography>
                </TableCell>
                <TableCell />
                {competitors.map((c) => {
                  const t = totals[c.id];
                  return (
                    <TableCell key={c.id}>
                      <Typography component="div" sx={{ fontSize: '0.85rem' }}>
                        Theirs <b>{fmtC(t.theirs)}</b> vs yours <b>{fmtC(t.oursMatched)}</b>
                      </Typography>
                      <Delta theirs={t.theirs} ours={t.oursMatched} />
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        )}

        <Typography component="div" sx={{ ...small, color: 'text.secondary', mt: 1.5 }}>
          Green means they charge more than you; red means they charge less.
          {computed.discountAmount > 0 && ` Your lines are before the quote's ${fmtC(computed.discountAmount)} discount.`}
          {' '}Framed by JG counts ADUs, guest houses and garages in their square footage. 805 Media Works only sells add-ons beside one of their main services.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
